/**
 * Subscription tier constants shared between frontend and server.
 * Single source of truth for tier names, quotas, and reset timing.
 */

import type { AnalysisMode } from './analysisMode.ts';

export const SUBSCRIPTION_TIERS = ['free', 'pro', 'premium', 'elite', 'admin'] as const;

export type SubscriptionTier = (typeof SUBSCRIPTION_TIERS)[number];

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
  elite: 'Elite',
  admin: 'Admin',
};

/** Check if a value is a valid SubscriptionTier */
export function isValidSubscriptionTier(value: unknown): value is SubscriptionTier {
  return typeof value === 'string' && (SUBSCRIPTION_TIERS as readonly string[]).includes(value);
}

// =============================================================================
// Per-Mode Picks Quotas
// =============================================================================

/** Per-mode daily limits for a tier */
export interface PicksQuotaConfig {
  standard: number;
  detailed: number;
  premium: number;
}

/** Daily pick generation limits per tier, per analysis mode */
export const TIER_PICKS_QUOTAS: Record<SubscriptionTier, PicksQuotaConfig> = {
  free: { standard: 2, detailed: 0, premium: 0 },
  pro: { standard: 10, detailed: 0, premium: 0 },
  premium: { standard: 25, detailed: 5, premium: 0 },
  elite: { standard: 30, detailed: 10, premium: 0 },
  admin: { standard: 999, detailed: 999, premium: 999 },
};

/** Get the daily limit for a specific tier + mode combination */
export function getPicksQuota(tier: SubscriptionTier, mode: AnalysisMode): number {
  return TIER_PICKS_QUOTAS[tier][mode];
}

/** Check if a tier can use a specific analysis mode at all (quota > 0) */
export function isModeAllowed(tier: SubscriptionTier, mode: AnalysisMode): boolean {
  return TIER_PICKS_QUOTAS[tier][mode] > 0;
}

/** Get all analysis modes a tier has access to */
export function getAllowedModes(tier: SubscriptionTier): AnalysisMode[] {
  return (['standard', 'detailed', 'premium'] as const).filter(
    (m) => TIER_PICKS_QUOTAS[tier][m] > 0,
  );
}

// =============================================================================
// Analysis Quota (flat — always uses Haiku)
// =============================================================================

/** Analysis quota = standard picks quota × this multiplier (rounded up) */
export const ANALYSIS_QUOTA_MULTIPLIER = 1.5;

/** Get daily analysis limit for a tier (derived from standard picks quota × 1.5) */
export function getAnalysisQuota(tier: SubscriptionTier): number {
  return Math.ceil(TIER_PICKS_QUOTAS[tier].standard * ANALYSIS_QUOTA_MULTIPLIER);
}

// =============================================================================
// Quota Response Types (shared between server and frontend)
// =============================================================================

/** Per-mode quota info for a single analysis mode */
export interface ModeQuotaInfo {
  mode: AnalysisMode;
  used: number;
  limit: number;
  remaining: number;
}

/** Per-mode picks quota usage (returned by /api/subscription/status) */
export interface PerModeQuotaUsage {
  modes: ModeQuotaInfo[];
  resetsAt: string;
  tier: SubscriptionTier;
  /** True when usage could not be fetched; render as unavailable, not real numbers. */
  unavailable?: boolean;
}

/** Flat analysis quota usage (returned by /api/subscription/status) */
export interface AnalysisQuotaUsage {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
  tier: SubscriptionTier;
  /** True when usage could not be fetched; render as unavailable, not real numbers. */
  unavailable?: boolean;
}

/** Get quota info for a specific mode from a PerModeQuotaUsage object */
export function getQuotaForMode(
  quota: PerModeQuotaUsage | null,
  mode: AnalysisMode,
): ModeQuotaInfo | null {
  if (!quota) return null;
  return quota.modes.find((m) => m.mode === mode) ?? null;
}

// =============================================================================
// Tier Pricing (single source of truth for display)
// =============================================================================

export interface TierPricingConfig {
  price: string;
  period: string;
}

/** Display pricing for each subscription tier */
export const TIER_PRICING: Record<SubscriptionTier, TierPricingConfig> = {
  free: { price: '$0', period: 'forever' },
  pro: { price: '$24.99', period: '/month' },
  premium: { price: '$39.99', period: '/month' },
  elite: { price: '$79.99', period: '/month' },
  admin: { price: '$0', period: '' },
};

/** Feature list for each subscription tier (for marketing/display) */
export const TIER_FEATURES: Record<SubscriptionTier, string[]> = {
  free: [
    `${TIER_PICKS_QUOTAS.free.standard} standard AI picks per day`,
    'Basic analysis',
    '5 tracked parlays per day',
    'Community leaderboard',
  ],
  pro: [
    `${TIER_PICKS_QUOTAS.pro.standard} standard AI picks per day`,
    '50 tracked parlays/day',
    'Best line comparison across sportsbooks',
    'Advanced analytics & scoring breakdown',
    'Social features (follow, share, friends)',
    'All themes',
  ],
  premium: [
    `${TIER_PICKS_QUOTAS.premium.standard} standard + ${TIER_PICKS_QUOTAS.premium.detailed} detailed AI picks per day`,
    'Detailed AI analysis mode',
    'Full scoring breakdown & confidence scores',
    'Line movement & injury alerts',
    'Community history for AI learning',
    'Custom themes',
  ],
  elite: [
    'Everything in Premium, plus:',
    `${TIER_PICKS_QUOTAS.elite.standard} standard + ${TIER_PICKS_QUOTAS.elite.detailed} detailed generations`,
    'AI Chat Assistant (conversational analysis)',
    'Sharp money signals & +EV badges',
    'Middles & sharp signal alerts',
    'Step-by-step prediction math',
  ],
  admin: ['Unlimited picks across all modes', 'Full platform access'],
};

// =============================================================================
// Reset Timing
// =============================================================================

/**
 * Daily quota resets at midnight ET (Eastern Time).
 * Using fixed EST offset (UTC-5) → 05:00 UTC.
 * Simpler than tracking DST; aligns with NBA schedule.
 */
export const QUOTA_RESET_HOUR_UTC = 5;

// =============================================================================
// Per-User Quota Overrides
// =============================================================================

/** Per-user override for any quota mode. null/missing = use tier default. */
export interface QuotaOverrides {
  standard?: number | null;
  detailed?: number | null;
  premium?: number | null;
  analysis?: number | null;
  tracking?: number | null;
}

/** Get effective picks quota, applying per-user override if set. */
export function getEffectivePicksQuota(
  tier: SubscriptionTier,
  mode: AnalysisMode,
  overrides?: QuotaOverrides | null,
): number {
  const override = overrides?.[mode];
  return override != null ? override : TIER_PICKS_QUOTAS[tier][mode];
}

/** Get effective analysis quota, applying per-user override if set. */
export function getEffectiveAnalysisQuota(
  tier: SubscriptionTier,
  overrides?: QuotaOverrides | null,
): number {
  if (overrides?.analysis != null) return overrides.analysis;
  if (overrides?.standard != null) return Math.ceil(overrides.standard * ANALYSIS_QUOTA_MULTIPLIER);
  return getAnalysisQuota(tier);
}
